import clsx from "clsx";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
import React, { useState } from "react";

import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import useTheme from "@material-ui/core/styles/useTheme";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import Loader from "components/Loader";
import Pagination from "components/Pagination";
import Popover from "components/Popover";
import { useRacehorse360Api } from "hooks/api";
import { MuiTheme } from "theme";
import { getPersonFullName } from "utils/person";

import { SortOrder } from "interfaces/SortOrder";
import useStyles from "./styles";

export interface Props {
  workoutRequest: racehorse360.IWorkoutRequest;
  isWorkoutRequestLoading: boolean;
}

const NextScheduledWorkout = (props: Props) => {
  const { workoutRequest, isWorkoutRequestLoading } = props;

  const PAGE_SIZE_UP_MD = 3;
  const PAGE_SIZE_DOWN_MD = 1;

  const classes = useStyles({
    facilityColor: workoutRequest?.facility?.backgroundColor,
    strokeColor: workoutRequest?.facility?.strokeColor
  });
  const theme = useTheme<MuiTheme>();
  const matchesUpMd = useMediaQuery(theme.breakpoints.up("md"));
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const { useListWorkoutRequestComments } = useRacehorse360Api();
  const { data: workoutRequestCommentsData, isLoading: isCommentLoading } =
    useListWorkoutRequestComments(
      {
        query: {
          workoutRequestIds: [workoutRequest?.id],
          isCreatedByTrainer: true
        },
        pagingOptions: {
          maxResults: 100
        },
        getOptions: {
          select: [
            "id",
            "body",
            "createdOn",
            "createdBy.firstName",
            "createdBy.lastName"
          ],
          orderBy: [`createdOn ${SortOrder.DESC}`]
        }
      },
      {
        enabled: Boolean(workoutRequest?.id)
      }
    );
  const workoutRequestComments: racehorse360.IWorkoutRequestComment[] =
    workoutRequestCommentsData?.workoutRequestComments || [];

  const isLoading = isWorkoutRequestLoading || isCommentLoading;
  const actualPageSize = matchesUpMd ? PAGE_SIZE_UP_MD : PAGE_SIZE_DOWN_MD;

  const handleViewCommentClick = (event: React.MouseEvent) => {
    setPopoverAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setPopoverAnchorEl(null);
  };

  const renderPopover = () => {
    const truncate = (text: string, maxLength: number = 200) => {
      return `${text.slice(0, maxLength)}${text.length > maxLength ? "…" : ""}`;
    };

    const slicedComments = workoutRequestComments.slice(
      (pageNumber - 1) * actualPageSize,
      (pageNumber - 1) * actualPageSize + actualPageSize
    );

    return (
      <Popover anchorEl={popoverAnchorEl} onClose={handlePopoverClose}>
        {Boolean(workoutRequestComments.length) && (
          <div className={classes.popover}>
            <span className={classes.popoverTitle}>
              Workout Request Comment
            </span>
            <div className={classes.popoverContent}>
              {slicedComments.map(comment => (
                <div key={comment.id} className={classes.comment}>
                  <span className={clsx(classes.commentAuthor)}>
                    {Boolean(comment.createdBy?.firstName) && (
                      <>
                        {comment.createdBy?.firstName.substring(0, 1)}
                        .&nbsp;
                      </>
                    )}
                    {Boolean(comment.createdBy?.lastName) && (
                      <>{comment.createdBy?.lastName}</>
                    )}
                  </span>
                  <span className={clsx(classes.commentDate)}>
                    {format(
                      parseISO(comment.createdOn),
                      "MMM d, yyyy - h:mm a"
                    )}
                  </span>
                  <span className={classes.commentText}>
                    {truncate(comment.body)}
                  </span>
                </div>
              ))}
            </div>
            {workoutRequestComments?.length > 1 && (
              <div className={classes.popoverFooter}>
                <Pagination
                  className={classes.paginator}
                  totalCount={workoutRequestComments.length}
                  onPageChange={setPageNumber}
                  pageSize={actualPageSize}
                  pageNumber={pageNumber}
                  showLegend={false}
                />
              </div>
            )}
          </div>
        )}
      </Popover>
    );
  };

  if (isLoading) {
    return <Loader />;
  }

  if (!workoutRequest) {
    return (
      <Paper className={classes.root}>
        <div className={classes.header}>
          <span className={clsx(classes.noWorkoutsComponentTitle)}>
            No Workouts Scheduled
          </span>
        </div>
      </Paper>
    );
  }

  return (
    <Paper className={classes.root}>
      <Grid container className={classes.header}>
        <Grid item>
          <span className={classes.componentTitle}>Next Scheduled Workout</span>
          <span className={classes.componentTitleUpMd}>
            Next Workout Request
          </span>
        </Grid>
      </Grid>
      <div className={classes.component}>
        <div className={classes.dateWrapper}>
          <div className={classes.date}>
            {format(
              parseISO(workoutRequest.date),
              matchesUpMd ? "MMMM dd" : "MMM dd"
            )}
          </div>
          <div className={classes.day}>
            {format(parseISO(workoutRequest.date), "iiii")}
          </div>
        </div>
        <div className={classes.card}>
          <div className={classes.cardContent}>
            <div className={classes.titleWrapper}>
              <span className={classes.title}>Workout Request</span>
              <span className={classes.titleDash}> - </span>
              <span className={classes.titleRequestedBy}>Requested by </span>
              <span>
                {getPersonFullName(workoutRequest.horse.trainer, "%f %m %L")}
                {Boolean(
                  workoutRequest.horse.trainer?.lastName ||
                    workoutRequest.horse.trainer?.firstName
                ) && <>&nbsp;on&nbsp;</>}
                {format(parseISO(workoutRequest.createdOn), "M/dd")}
              </span>
            </div>
            <div className={classes.facility}>
              {workoutRequest.facility.name}
            </div>
            {Boolean(workoutRequestComments.length) && (
              <div
                className={classes.commentButton}
                onClick={handleViewCommentClick}
              >
                View Request Comment
              </div>
            )}
          </div>
        </div>
        {renderPopover()}
      </div>
    </Paper>
  );
};

export default NextScheduledWorkout;
